import compact from "lodash.compact";

import {
  AudienceDto,
  AudienceExpressionPayload,
  AudienceFilterDto,
  AudienceFilterType,
  ChannelType,
  CountryEnum,
  DeviceType,
  ExpressionNodeQueryType,
  PurchaseLikelihoodType,
  UrlMatch,
  VisitorType,
} from "@intelligems/ig-types";
import { capitalizeFirstLetter } from "@intelligems/ig-utils";

export const getInitialVisitorState = (
  state: AudienceDto,
  queryParamIndex: number
) => {
  return (
    state?.filters[queryParamIndex]?.expression
      ?.map((expression) => expression.query)
      .find((query) => query?.type === "visitor")?.value ?? "any"
  );
};

export const getInitialChannelsState = (
  state: AudienceDto,
  queryParamIndex: number
) => {
  const valArray = state?.filters[queryParamIndex]?.expression
    ?.map((expression) => expression.query)
    .filter((query) => query?.type === "trafficSource")
    .map((query) => query?.value)
    .filter((val) => val);
  return valArray?.length ? valArray : null;
};

export const getInitialCountriesState = (
  state: AudienceDto,
  queryParamIndex: number
) => {
  return (
    (state?.filters[queryParamIndex]?.expression
      ?.map((expression) => expression.query)
      .filter((query) => query?.type === "country")
      .map((query) => query?.value)
      .filter((val) => val) as string[]) ?? []
  );
};

export const getInitialDeviceState = (
  state: AudienceDto,
  queryParamIndex: number
) => {
  return (
    state?.filters[queryParamIndex]?.expression
      ?.map((expression) => expression.query)
      .find((query) => query?.type === "device")?.value ?? "any"
  );
};
export const getInitialPurchaseLikelihoodState = (
  state: AudienceDto,
  queryParamIndex: number
) => {
  const expression = state?.filters[queryParamIndex]?.expression;
  if (!expression) return null;

  const values: string[] = [];
  for (const { query } of expression) {
    if (query?.type === "purchaseLikelihood" && query.value) {
      values.push(query.value);
    }
  }
  return values.length ? values : null;
};

export const getExpressionsState = (
  deviceState: DeviceType | null,
  visitorState: VisitorType | null,
  countryState: string[],
  channelState: ChannelType[] | null,
  purchaseLikelihoodState: PurchaseLikelihoodType[] | null
): AudienceExpressionPayload[] => {
  const expressions: AudienceExpressionPayload[] = [];
  if (deviceState && deviceState !== "any") {
    expressions.push({
      type: "device",
      queries: [
        {
          key: "device",
          value: deviceState,
          filter: "equals",
          type: "device",
        },
      ],
    });
  }
  if (visitorState && visitorState !== "any") {
    expressions.push({
      type: "visitor",
      queries: [
        {
          key: "visitor",
          value: visitorState,
          filter: "equals",
          type: "visitor",
        },
      ],
    });
  }
  if (purchaseLikelihoodState && purchaseLikelihoodState.length) {
    expressions.push({
      type: "purchaseLikelihood",
      queries: purchaseLikelihoodState.map((purchaseLikelihood) => ({
        key: "purchaseLikelihood",
        value: purchaseLikelihood,
        filter: "equals",
        type: "purchaseLikelihood",
      })),
    });
  }
  if (channelState && channelState.length) {
    expressions.push({
      type: "trafficSource",
      queries: channelState.map((channel) => ({
        key: "trafficSource",
        value: channel,
        filter: "equals",
        type: "trafficSource",
      })),
    });
  }
  if (countryState.length) {
    expressions.push({
      type: "country",
      queries: countryState.map((country) => ({
        key: "country",
        value: country,
        filter: "equals",
        type: "country",
      })),
    });
  }
  return expressions;
};

export const formatCustomQueriesChip = (queryParam: AudienceFilterDto) => {
  const queriesOnly = compact(
    queryParam?.expression?.map((expression) => expression.query) ?? []
  );
  const expressionsLength = queriesOnly.length;
  return `Custom (${capitalizeFirstLetter(queriesOnly[0].value)} ${
    expressionsLength - 1 > 0 ? "+" : ""
  } ${expressionsLength - 1 > 0 ? expressionsLength - 1 : ""} ${
    expressionsLength - 1 > 0 ? "more" : ""
  })`;
};
export const formatQueries = (
  queries: ExpressionNodeQueryType[],
  groupLabelCategories?: boolean
) => {
  let countryLength = 0;
  let shouldShorten = false;
  if (queries[0].type === "country") {
    countryLength = queries.length;
  }
  if (countryLength > 3) {
    shouldShorten = true;
  }
  if (queries[0].type === "country" && groupLabelCategories) {
    return "Country";
  }
  if (queries[0].type === "trafficSource" && groupLabelCategories) {
    return "Source";
  }
  if (queries[0].type === "purchaseLikelihood" && groupLabelCategories) {
    return "Purchase Likelihood";
  }

  // Bare Purchase Likelihood bucket names read ambiguously in a rule sentence
  // ("If audience is Medium"); every other dimension's values are
  // self-explanatory on their own (e.g. "Desktop", "New").
  const purchaseLikelihoodPrefix =
    queries[0].type === "purchaseLikelihood" ? "Purchase Likelihood " : "";
  return `${purchaseLikelihoodPrefix}${queries
    .slice(0, shouldShorten ? 4 : queries.length)
    .map((query) =>
      CountryEnum[query.value]
        ? `${CountryEnum[query.value]} (${query.value})`
        : capitalizeFirstLetter(query.value)
    )
    .join(", ")
    .replace(/, ([^,]*)$/, " or $1")} ${
    shouldShorten && queries.length !== 4 ? "+" : ""
  } ${shouldShorten && queries.length !== 4 ? queries.length - 4 : ""} ${
    shouldShorten && queries.length !== 4 ? "more" : ""
  }`;
};
